<template>
  <div>
    <request-specialist-sidebar
      :visible="requestSidebarVisible"
      :customer="selectedCustomer"
      @change="handleRequestSidebarChange"
      @hidden="selectedCustomer = null"
      @refresh="handleSidebarRefresh"
    />

    <b-card no-body class="mb-0">
      <b-card-header class="pb-50">
        <h5>Filtros</h5>
      </b-card-header>

      <b-card-body>
        <b-row>
          <b-col cols="12" md="4" class="mb-1">
            <dynamic-select
              id="requestSpecialistCustomer"
              label="Nome do cliente"
              placeholder="Digite o nome do cliente"
              v-model="filters.customer_id"
              :options="customerOptions"
              :loading="loadingCustomers"
              @find="findCustomers"
            />
          </b-col>

          <b-col cols="12" md="4" class="mb-1">
            <b-form-group label="CPF ou CNPJ" label-for="requestSpecialistDocument">
              <b-form-input
                id="requestSpecialistDocument"
                v-model="filters.document"
                @keyup.enter="searchCustomers"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="4" class="mb-1">
            <b-form-group label="E-mail" label-for="requestSpecialistEmail">
              <b-form-input
                id="requestSpecialistEmail"
                v-model="filters.email"
                @keyup.enter="searchCustomers"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="4" class="mb-1">
            <b-form-group label="PN dono" label-for="requestSpecialistOwner">
              <v-select
                id="requestSpecialistOwner"
                v-model="filters.owner_consultant_ids"
                :reduce="consultant => consultant.id"
                :options="ownerConsultants"
                label="name"
                multiple
                dir="ltr"
              >
                <template #no-options>
                  Sem resultados
                </template>
              </v-select>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="4" class="mb-1">
            <b-form-group label="Unidade de Negócio" label-for="requestSpecialistDepartmentFilter">
              <v-select
                id="requestSpecialistDepartmentFilter"
                v-model="filters.department_id"
                :reduce="department => department.id"
                :options="businessUnits"
                label="name"
                dir="ltr"
                @input="handleDepartmentFilterChange"
              >
                <template #no-options>
                  Sem resultados
                </template>
              </v-select>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="4" class="mb-1">
            <b-form-group label="Especialista atribuído" label-for="requestSpecialistConsultantFilter">
              <v-select
                id="requestSpecialistConsultantFilter"
                v-model="filters.specialist_consultant_id"
                :reduce="consultant => consultant.id"
                :options="filterConsultants"
                label="name"
                dir="ltr"
              >
                <template #no-options>
                  Sem resultados
                </template>
              </v-select>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="4" class="mb-1">
            <b-form-group label="Status" label-for="requestSpecialistStatusFilter">
              <v-select
                id="requestSpecialistStatusFilter"
                v-model="filters.status"
                :reduce="status => status.value"
                :options="statusOptions"
                label="label"
                multiple
                dir="ltr"
              >
                <template #no-options>
                  Sem resultados
                </template>
              </v-select>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="4" class="mb-1">
            <b-form-group label="Solicitação anterior" label-for="requestSpecialistPreviousFilter">
              <v-select
                id="requestSpecialistPreviousFilter"
                v-model="filters.has_previous_request"
                :reduce="option => option.value"
                :options="hasPreviousRequestOptions"
                label="label"
                dir="ltr"
              >
                <template #no-options>
                  Sem resultados
                </template>
              </v-select>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="4" class="mb-1">
            <b-form-group label="Tipo de visualização" label-for="requestSpecialistStructure">
              <v-select
                id="requestSpecialistStructure"
                v-model="filters.structure_option"
                :reduce="structure => structure.key"
                :options="structureTypes"
                label="name"
                dir="ltr"
                :clearable="false"
                @input="handleStructureFilterChange"
              >
                <template #no-options>
                  Sem resultados
                </template>
              </v-select>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="3" class="mb-1">
            <b-form-group label="Solicitado em" label-for="requestSpecialistRequestedRange">
              <flat-pickr
                id="requestSpecialistRequestedRange"
                v-model="requestedDateRange"
                class="form-control"
                :config="flatPickrConfig"
                :placeholder="`01 Jan 2020 até 31 Dez ${actualYear}`"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="3" class="mb-1">
            <b-form-group label="Atribuído em" label-for="requestSpecialistAssignedRange">
              <flat-pickr
                id="requestSpecialistAssignedRange"
                v-model="assignedDateRange"
                class="form-control"
                :config="flatPickrConfig"
                :placeholder="`01 Jan 2020 até 31 Dez ${actualYear}`"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-body>

      <div class="m-2">
        <b-row>
          <b-col
            cols="12"
            md="4"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label class="mb-0">Exibir</label>
            <v-select
              v-model="itemsPerPage"
              dir="ltr"
              :options="itemsPerPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
              @input="handleItemsPerPageChange"
            >
              <template #no-options>
                Sem resultados
              </template>
            </v-select>
            <label class="mb-0">itens</label>
          </b-col>

          <b-col cols="12" md="8">
            <div class="d-flex align-items-center justify-content-end flex-wrap">
              <span class="text-muted mr-1 mb-1 mb-md-0">
                Selecionados: {{ selectedCustomerIds.length }}
              </span>

              <b-button
                variant="outline-secondary"
                class="mr-1 mb-1 mb-md-0"
                :disabled="loading"
                @click.prevent="clearFilters"
              >
                Limpar filtros
              </b-button>

              <b-button
                variant="primary"
                class="mr-1 mb-1 mb-md-0"
                :disabled="loading"
                @click.prevent="searchCustomers"
              >
                <b-spinner
                  v-if="loading"
                  small
                />
                <feather-icon
                  v-else
                  icon="SearchIcon"
                  class="mr-50"
                />
                <span>{{ loading ? 'Aguarde...' : 'Pesquisar' }}</span>
              </b-button>

              <b-button
                variant="outline-primary"
                class="mb-1 mb-md-0"
                :disabled="loading || assigning || !hasSelectedCustomers"
                @click.prevent="openAssignSidebar"
              >
                <b-spinner
                  v-if="assigning"
                  small
                />
                <feather-icon
                  v-else
                  icon="ClipboardIcon"
                  class="mr-50"
                />
                <span>Solicitar para selecionados</span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        hover
        responsive
        :items="items"
        :fields="tableColumns"
        primary-key="customer_id"
        show-empty
        empty-text="Não foram encontrados clientes para esta busca"
        :busy="loading"
        :no-local-sorting="true"
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        @sort-changed="handleSort"
      >
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle mr-50" />
            <strong>Carregando...</strong>
          </div>
        </template>

        <template #head(selection)>
          <b-form-checkbox
            :checked="allVisibleSelected"
            @change="toggleVisibleSelection"
          />
        </template>

        <template #cell(selection)="data">
          <b-form-checkbox
            :checked="isCustomerSelected(data.item.customer_id)"
            @change="toggleCustomerSelection(data.item.customer_id, $event)"
            @click.native.stop
          />
        </template>

        <template #cell(customer_name)="data">
          <b-button
            variant="link"
            class="p-0 text-left"
            @click.stop="goToCustomerDetails(data.item)"
          >
            {{ data.item.customer_name }}
          </b-button>
        </template>

        <template #cell(owner_name)="data">
          <span>{{ data.item.owner_name || '-' }}</span>
        </template>

        <template #cell(total_requests)="data">
          <span>{{ data.item.total_requests || 0 }}</span>
        </template>

        <template #cell(active_requests)="data">
          <div
            v-if="data.item.active_requests && data.item.active_requests.length"
            class="d-flex flex-column align-items-start"
          >
            <b-badge
              v-for="requestItem in data.item.active_requests"
              :key="requestItem.id"
              :variant="statusVariant(requestItem.status)"
              class="mb-50 text-left text-wrap"
            >
              {{ requestItem.department_name }}:
              {{ requestItem.specialist_label || 'Sem especialista' }}
            </b-badge>
          </div>
          <span v-else>-</span>
        </template>

        <template #cell(latest_requested_at)="data">
          <span>{{ formatDateTime(data.item.latest_requested_at) }}</span>
        </template>

        <template #cell(latest_assigned_at)="data">
          <span>{{ formatDateTime(data.item.latest_assigned_at) }}</span>
        </template>

        <template #cell(latest_status)="data">
          <b-badge
            v-if="data.item.latest_status"
            :variant="statusVariant(data.item.latest_status)"
          >
            {{ statusLabel(data.item.latest_status) }}
          </b-badge>
          <span v-else>-</span>
        </template>

        <template #cell(actions)="data">
          <b-button
            :id="`manage-request-specialist` + data.item.customer_id"
            class="btn-icon"
            variant="flat"
            style="padding: 0; color: white"
            @click="openRequestSidebar(data.item)"
          >
            <feather-icon class="text-body" icon="EditIcon" />
          </b-button>
          <b-tooltip
            :target="`manage-request-specialist` + data.item.customer_id"
            triggers="hover"
          >
            Gerenciar solicitação
          </b-tooltip>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">
              Exibindo {{ pageItemsFrom }} a {{ pageItemsTo }} de
              {{ totalItems }} itens
            </span>
          </b-col>

          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalItems"
              :per-page="itemsPerPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              @change="loadCustomers"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <b-sidebar
      id="request-specialist-assign-sidebar"
      sidebar-class="sidebar-lg"
      :visible="assignSidebarVisible"
      bg-variant="white"
      shadow
      backdrop
      no-header
      right
      @change="handleAssignSidebarChange"
      @hidden="resetAssignSidebar"
    >
      <template #default="{ hide }">
        <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
          <h4>Solicitar atendimento prioritário</h4>
          <feather-icon
            class="ml-1 cursor-pointer"
            icon="XIcon"
            size="16"
            @click="hide"
          />
        </div>

        <div class="p-2">
          <p class="mb-2">
            Clientes selecionados: {{ selectedCustomerIds.length }}
          </p>

          <b-form-group
            label="Unidade de Negócio"
            label-for="requestSpecialistAssignDepartment"
          >
            <v-select
              id="requestSpecialistAssignDepartment"
              v-model="assignForm.department_id"
              :reduce="department => department.id"
              :options="businessUnits"
              :clearable="false"
              label="name"
              dir="ltr"
              @input="handleAssignDepartmentChange"
            >
              <template #no-options>
                Sem resultados
              </template>
            </v-select>
          </b-form-group>

          <b-form-group
            label="Especialista"
            label-for="requestSpecialistAssignConsultant"
          >
            <v-select
              id="requestSpecialistAssignConsultant"
              v-model="assignForm.specialist_consultant_id"
              :reduce="consultant => consultant.id"
              :options="assignConsultants"
              label="name"
              dir="ltr"
            >
              <template #no-options>
                Sem resultados
              </template>
            </v-select>
          </b-form-group>

          <b-form-group
            label="Observações"
            label-for="requestSpecialistAssignNotes"
          >
            <b-form-textarea
              id="requestSpecialistAssignNotes"
              v-model="assignForm.notes"
              rows="3"
              max-rows="5"
            />
          </b-form-group>

          <div class="d-flex mt-2">
            <b-button
              variant="primary"
              class="mr-2"
              :disabled="assigning || !assignForm.department_id"
              @click="assignSpecialist"
            >
              <b-spinner
                v-if="assigning"
                small
              />
              <span>{{ assigning ? 'Aguarde...' : 'Solicitar' }}</span>
            </b-button>
          </div>
        </div>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import {
  BButton,
  BCard,
  BCardBody,
  BCardHeader,
  BCol,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BPagination,
  BRow,
  BSidebar,
  BSpinner,
  BTable,
  BBadge,
  BTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import { Portuguese } from 'flatpickr/dist/l10n/pt.js'
import ShortcutButtonsPlugin from 'shortcut-buttons-flatpickr'
import todayButton from '@/helpers/todayButton'
import { mapActions, mapGetters } from 'vuex'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import * as accountTypes from '@/modules/account/store/types'
import * as sharedTypes from '@/modules/shared/store/types'
import * as customerTypes from '@/modules/customer/store/types'
import { OWN, SPECIALIST, STRUCTURE } from '@/constants/structure_options'
import { isPnRole } from '@/constants/auth'
import RequestSpecialistSidebar from '@/modules/customer/components/RequestSpecialistSidebar.vue'
import DynamicSelect from '@/modules/shared/components/DynamicSelect.vue'
import {
  getRequestSpecialistStatusLabel,
  getRequestSpecialistStatusVariant,
  REQUEST_SPECIALIST_STATUS_OPTIONS,
} from '@/constants/requestSpecialist'

const HOLDINGS_DEPARTMENT_ID = 15

export default {
  components: {
    BBadge,
    BButton,
    BCard,
    BCardBody,
    BCardHeader,
    BCol,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BPagination,
    BRow,
    BSidebar,
    BSpinner,
    BTable,
    BTooltip,
    RequestSpecialistSidebar,
    DynamicSelect,
    vSelect,
    flatPickr,
  },
  setup() {
    const actualYear = new Date().getFullYear()
    return { toast: useToast(), actualYear }
  },
  data() {
    return {
      loading: false,
      assigning: false,
      currentPage: 1,
      itemsPerPage: 10,
      items: [],
      totalItems: 0,
      pageItemsFrom: 0,
      pageItemsTo: 0,
      sortBy: null,
      sortDesc: true,
      selectedCustomerIds: [],
      requestSidebarVisible: false,
      selectedCustomer: null,
      assignSidebarVisible: false,
      businessUnits: [],
      filterConsultants: [],
      assignConsultants: [],
      consultantsCache: {},
      loadingCustomers: false,
      customerOptions: [],
      flatPickrConfig: {
        altFormat: 'j M Y',
        altInput: true,
        mode: 'range',
        dateFormat: 'Y-m-d',
        locale: Portuguese,
        plugins: [ShortcutButtonsPlugin(todayButton)],
      },
      requestedDateRange: null,
      assignedDateRange: null,
      filters: {
        customer_id: null,
        document: '',
        email: '',
        owner_consultant_ids: [],
        department_id: null,
        status: [],
        specialist_consultant_id: null,
        has_previous_request: null,
        requested_at_start: null,
        requested_at_end: null,
        assigned_at_start: null,
        assigned_at_end: null,
        structure_option: STRUCTURE,
      },
      assignForm: {
        department_id: null,
        specialist_consultant_id: null,
        notes: '',
      },
      hasPreviousRequestOptions: [
        { value: true, label: 'Com solicitação anterior' },
        { value: false, label: 'Sem solicitação anterior' },
      ],
      tableColumns: [
        { key: 'selection', label: '', class: 'text-center' },
        { key: 'customer_name', label: 'Cliente', sortable: true },
        { key: 'email', label: 'E-mail', sortable: true },
        { key: 'owner_name', label: 'PN dono', sortable: true },
        { key: 'total_requests', label: 'Total de solicitações', sortable: true },
        { key: 'active_requests', label: 'Solicitações ativas' },
        { key: 'latest_requested_at', label: 'Última solicitação', sortable: true },
        { key: 'latest_assigned_at', label: 'Última atribuição', sortable: true },
        { key: 'latest_status', label: 'Status mais recente' },
        { key: 'actions', label: 'Ações' },
      ],
    }
  },
  computed: {
    ...mapGetters({
      initialItemsPerPage: sharedTypes.INITIAL_ITEMS_PER_PAGE,
      itemsPerPageOptions: sharedTypes.ITEMS_PER_PAGE_OPTIONS,
      ownerConsultants: sharedTypes.CONSULTANTS,
      structureTypes: sharedTypes.STRUCTURE_TYPES,
      user: accountTypes.USER,
    }),
    requestStructureOption() {
      if (Number(this.user?.department_id) === HOLDINGS_DEPARTMENT_ID) {
        return SPECIALIST
      }

      return isPnRole(this.user?.user_contract_type_id) ? OWN : STRUCTURE
    },
    hasSelectedCustomers() {
      return this.selectedCustomerIds.length > 0
    },
    allVisibleSelected() {
      if (!this.items.length) {
        return false
      }

      return this.items.every(item => this.selectedCustomerIds.includes(item.customer_id))
    },
    statusOptions() {
      return REQUEST_SPECIALIST_STATUS_OPTIONS
    },
  },
  watch: {
    requestedDateRange(value) {
      this.applyDateRange(value, 'requested_at_start', 'requested_at_end')
    },
    assignedDateRange(value) {
      this.applyDateRange(value, 'assigned_at_start', 'assigned_at_end')
    },
  },
  async mounted() {
    this.itemsPerPage = this.initialItemsPerPage
    this.filters.structure_option = this.requestStructureOption

    // A lista é o conteúdo principal: dispara já, sem esperar os dados dos filtros.
    this.loadCustomers()

    // Dados dos filtros (consultores, estruturas, unidades) carregam em paralelo,
    // sem travar o carregamento inicial da tabela.
    await Promise.all([
      this.getConsultants(),
      this.getStructures(),
      this.loadBusinessUnits(),
      this.loadFilterConsultants(),
    ])
  },
  methods: {
    ...mapActions({
      getConsultants: sharedTypes.GET_CONSULTANTS,
      getStructures: sharedTypes.GET_STRUCTURE_TYPES,
      fetchBusinessUnits: customerTypes.REQUEST_SPECIALIST_BUSINESS_UNITS,
      fetchRequestSpecialistConsultants: customerTypes.REQUEST_SPECIALIST_CONSULTANTS,
      fetchRequestSpecialistCustomers: customerTypes.REQUEST_SPECIALIST_CUSTOMERS,
      searchRequestSpecialist: customerTypes.SEARCH_REQUEST_SPECIALIST,
      assignRequestSpecialist: customerTypes.ASSIGN_REQUEST_SPECIALIST,
    }),
    async findCustomers(keyword) {
      this.loadingCustomers = true

      try {
        const response = await this.fetchRequestSpecialistCustomers({ keyword })
        this.customerOptions = response.data || []
      } catch (error) {
        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Oops!',
            text: 'Ocorreu um erro ao carregar os clientes.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.loadingCustomers = false
      }
    },
    applyDateRange(value, startKey, endKey) {
      const [start, end] = (value || '').split(' até ')
      this.filters[startKey] = start || null
      this.filters[endKey] = end || null
    },
    async loadBusinessUnits() {
      const response = await this.fetchBusinessUnits()
      this.businessUnits = response.data || []
    },
    async loadFilterConsultants() {
      const params = {
        assigned_only: 1,
        structure_option: this.filters.structure_option,
      }

      if (this.filters.department_id) {
        params.department_id = this.filters.department_id
      }

      const response = await this.fetchRequestSpecialistConsultants(params)
      this.filterConsultants = response.data || []
    },
    async fetchConsultants(departmentId = null) {
      const cacheKey = departmentId || 'all'

      if (this.consultantsCache[cacheKey]) {
        return this.consultantsCache[cacheKey]
      }

      const response = await this.fetchRequestSpecialistConsultants(
        departmentId ? { department_id: departmentId } : {},
      )

      this.consultantsCache = {
        ...this.consultantsCache,
        [cacheKey]: response.data || [],
      }

      return this.consultantsCache[cacheKey]
    },
    async loadCustomers(page = this.currentPage) {
      this.loading = true

      try {
        const response = await this.searchRequestSpecialist({
          page,
          params: {
            ...this.filters,
            limit: this.itemsPerPage,
            order: this.buildOrderParam(),
          },
        })

        const payload = response.data || {}
        this.items = payload.data || []
        this.totalItems = payload.total || 0
        this.pageItemsFrom = payload.from || 0
        this.pageItemsTo = payload.to || 0
        this.currentPage = payload.current_page || page
      } catch (error) {
        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Oops!',
            text: 'Ocorreu um erro ao carregar os clientes.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },
    async handleDepartmentFilterChange() {
      this.filters.specialist_consultant_id = null
      await this.loadFilterConsultants()
    },
    async handleStructureFilterChange() {
      this.filters.specialist_consultant_id = null
      await this.loadFilterConsultants()
    },
    async handleAssignDepartmentChange(departmentId) {
      this.assignForm.specialist_consultant_id = null
      this.assignConsultants = await this.fetchConsultants(departmentId)

      if (this.assignConsultants.length === 1) {
        this.assignForm.specialist_consultant_id = this.assignConsultants[0].id
      }
    },
    async openAssignSidebar() {
      this.assignSidebarVisible = true
    },
    async assignSpecialist() {
      this.assigning = true

      try {
        await this.assignRequestSpecialist({
          customer_ids: this.selectedCustomerIds,
          department_id: this.assignForm.department_id,
          specialist_consultant_id: this.assignForm.specialist_consultant_id,
          notes: this.assignForm.notes || null,
        })

        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Sucesso!',
            text: 'Solicitações registradas com sucesso.',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        this.assignSidebarVisible = false
        this.selectedCustomerIds = []
        await Promise.all([
          this.loadCustomers(this.currentPage),
          this.loadFilterConsultants(),
        ])
      } catch (error) {
        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Oops!',
            text: error?.response?.data?.message
              || 'Ocorreu um erro ao registrar as solicitações.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.assigning = false
      }
    },
    searchCustomers() {
      // Nova busca muda o conjunto de clientes listados: limpa a seleção anterior
      // para não enviar clientes que não estão mais visíveis no payload de atribuição.
      this.selectedCustomerIds = []
      this.loadCustomers(1)
    },
    async clearFilters() {
      this.filters = {
        customer_id: null,
        document: '',
        email: '',
        owner_consultant_ids: [],
        department_id: null,
        status: [],
        specialist_consultant_id: null,
        has_previous_request: null,
        requested_at_start: null,
        requested_at_end: null,
        assigned_at_start: null,
        assigned_at_end: null,
        structure_option: this.requestStructureOption,
      }
      this.requestedDateRange = null
      this.assignedDateRange = null
      this.currentPage = 1
      await this.loadFilterConsultants()
      this.searchCustomers()
    },
    handleItemsPerPageChange() {
      this.currentPage = 1
      this.loadCustomers(1)
    },
    handleSort({ sortBy, sortDesc }) {
      this.sortBy = sortBy
      this.sortDesc = sortDesc
      this.currentPage = 1
      this.loadCustomers(1)
    },
    buildOrderParam() {
      if (!this.sortBy) {
        return null
      }

      return `${this.sortBy} ${this.sortDesc ? 'DESC' : 'ASC'}`
    },
    toggleVisibleSelection(checked) {
      const visibleIds = this.items.map(item => item.customer_id)

      if (checked) {
        this.selectedCustomerIds = Array.from(
          new Set([...this.selectedCustomerIds, ...visibleIds]),
        )
        return
      }

      this.selectedCustomerIds = this.selectedCustomerIds.filter(
        customerId => !visibleIds.includes(customerId),
      )
    },
    toggleCustomerSelection(customerId, checked) {
      if (checked) {
        this.selectedCustomerIds = Array.from(
          new Set([...this.selectedCustomerIds, customerId]),
        )
        return
      }

      this.selectedCustomerIds = this.selectedCustomerIds.filter(
        selectedId => selectedId !== customerId,
      )
    },
    isCustomerSelected(customerId) {
      return this.selectedCustomerIds.includes(customerId)
    },
    goToCustomerDetails(item) {
      this.$router.push({
        name: 'customer-profile',
        params: { id: item.customer_id },
      })
    },
    openRequestSidebar(item) {
      this.selectedCustomer = {
        id: item.customer_id,
        name: item.customer_name,
      }
      this.requestSidebarVisible = true
    },
    handleRequestSidebarChange(visible) {
      this.requestSidebarVisible = visible
    },
    async handleSidebarRefresh() {
      await Promise.all([
        this.loadCustomers(this.currentPage),
        this.loadFilterConsultants(),
      ])
    },
    handleAssignSidebarChange(visible) {
      this.assignSidebarVisible = visible
    },
    resetAssignSidebar() {
      this.assignForm = {
        department_id: null,
        specialist_consultant_id: null,
        notes: '',
      }
      this.assignConsultants = []
    },
    statusLabel(status) {
      return getRequestSpecialistStatusLabel(status)
    },
    statusVariant(status) {
      return getRequestSpecialistStatusVariant(status)
    },
    formatDateTime(value) {
      if (!value) {
        return '-'
      }

      return new Intl.DateTimeFormat('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(new Date(value))
    },
  },
}
</script>

<style lang="scss">
$themeColor: #f26237;
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
